"use client";

import type React from "react";

import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input as DefaultInput, InputVariants } from "@/components/ui/default-input";
import { Button } from "@/components/ui/button";

interface CustomInputProps 
  extends React.InputHTMLAttributes<HTMLInputElement>,
  InputVariants 
      {
        startContent?: React.ReactNode;
        endContent?: React.ReactNode;
        helperText?: string;
        label?: string;
        error?: string;
      }

export function Input({
  className,
  type,
  startContent,
  endContent,
  helperText,
  label,
  error,
  ...props
}: CustomInputProps) {
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === "password";
  const inputType = isPassword && showPassword ? "text" : type;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="w-full space-y-2">
      {label && (
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
        </label>
      )}

      <div className="relative flex items-center">
        {startContent && (
          <div className="absolute left-3 flex h-full items-center pointer-events-none">
            {startContent}
          </div>
        )}

        <DefaultInput
          type={inputType}
          className={cn(
            startContent && "pl-10",
            (isPassword || endContent) && "pr-10",
            error && "border-destructive",
            className
          )}
          {...props}
        />

        {(isPassword || endContent) && (
          <div className="absolute right-3 flex h-full items-center">
            {isPassword ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                <span className="sr-only">
                  {showPassword ? "Hide password" : "Show password"}
                </span>
              </Button>
            ) : (
              endContent
            )}
          </div>
        )}
      </div>

      {(helperText || error) && (
        <p
          className={cn(
            "text-sm",
            error ? "text-destructive" : "text-muted-foreground"
          )}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
}
